import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import axiosClient from "@service/axiosClient";
import Tabs from "@mui/material/Tabs";
import Tab, { TabProps } from "@mui/material/Tab";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { styled } from "@mui/material/styles";

import Button from "@components/Button";
import * as FileInput from "@components/FileInput";
import { IPlantDTO } from "@interfaces/IPlantDTO";
import { HoursEdit } from "@components/hours/HoursEdit";
import { GeneratorTemplate } from "@components/hours/GeneratorTemplate";
import { useAuth } from "@components/context/AuthContext";
import { IRoleDTO } from "@interfaces/IRoleDTO";
import Spinner from "@components/Spinner";
export interface IPlantSelect {
  id: number;
  value: string;
}

const AntTabs = styled(Tabs)({
  borderBottom: "1px solid #e8e8e8",
  "& .MuiTabs-indicator": {
    backgroundColor: "#1890ff",
  },
});

const AntTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    minWidth: 0,
    [theme.breakpoints.up("sm")]: {
      minWidth: 0,
    },
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(1),
    color: "rgba(0, 0, 0, 0.85)",
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    "&:hover": {
      color: "rgb(22, 102, 194)",
      opacity: 1,
    },
    "&.Mui-selected": {
      color: "rgb(22, 102, 194)",
      fontWeight: theme.typography.fontWeightMedium,
    },
    "&.Mui-focusVisible": {
      backgroundColor: "#d1eaff",
    },
  })
);

export function HoursUpload(): JSX.Element {
  const { hasPermission } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [plant_id] = useState<number>(1);

  const [plants, setPlants] = useState<IPlantSelect[]>([]);
  const [roles, setRoles] = useState<IRoleDTO[]>([]);

  const [selectedFile, setSelectedFile] = useState<File[] | null>(null);
  const [fileStatus, setFileStatus] = useState<string[]>([]);
  const [uploadingHours, setUploadingHours] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  const [tabIndex, setTabIndex] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState<{
    title: string;
    file: string;
    message: string[];
    status?: string | string[];
  }>({
    title: "",
    file: "",
    message: [],
    status: "",
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    async function fetchPlants() {
      setIsLoading(false);
      try {
        const response = await axiosClient.get("/auth/info/admin");
        setPlants(
          (response.data.plants ?? []).map((plant: IPlantDTO) => ({
            id: plant.id,
            value: plant.name,
          }))
        );
        if (response.data.roles) {
          setRoles(response.data.roles);
        }
      } catch (error) {
        console.error("Error fetching plants:", error);
      }
    }
    if (isLoading) {
      fetchPlants();
    }
  }, [isLoading]);

  function handleSelectedFiles(files: File[]) {
    setDialogOpen(false);
    setSelectedFile(files.length > 0 ? files : null);
    if (files.length > 0) {
      setFileStatus(new Array(files.length).fill("progress"));
    } else {
      setFileStatus([]);
    }
  }
  const handleSendHours = useCallback(async () => {
    if (!selectedFile || selectedFile.length <= 0) {
      setDialogMessage({
        title: "Error",
        file: "",
        message: ["Please select a file first."],
        status: ["error"],
      });
      setDialogOpen(true);
      return;
    }
    setDialogOpen(false);
    setUploadingHours(true);
    const messages: {
      index: number;
      file: string;
      message: string;
      status: string;
    }[] = []; // Array to store messages for each file
    await Promise.all(
      selectedFile.map(async (file, index) => {
        try {
          let formData = new FormData();
          formData.append("plant_hours", file);
          formData.append("plant_id", plant_id.toString()); // if needed
          await axiosClient.post("/hours/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          messages.push({
            index,
            file: file.name,
            message: "File uploaded successfully!",
            status: "success",
          });
        } catch (err) {
          messages.push({
            index,
            file: file.name,
            message: `${
              (err as any)?.response?.data?.error ||
              (err as any)?.response?.data?.warning ||
              (err as any)?.message ||
              "An unknown error occurred."
            }`,
            status: (err as any)?.response?.data?.error
              ? "error"
              : (err as any)?.response?.data?.warning
              ? "warning"
              : "message",
          });
        }
      })
    );
    setDialogMessage({
      title: "Upload Results",
      file: "",
      message: messages.map((msg) => msg.message),
      status: messages.map((msg) => msg.status),
    });
    setFileStatus(messages.map((msg) => msg.status));
    setDialogOpen(true);
    setUploadingHours(false);
    setRefresh((prev) => !prev);
  }, [selectedFile, plant_id]);

  return (
    <div className="">
      <Box
        className="tw-flex tw-flex-col tw-w-full tw-h-full tw-ml-40"
        display="flex"
        alignItems="left"
      >
        <div className="tw-flex tw-flex-col">
          {/* TABS */}
          <Box sx={{ borderColor: "divider" }}>
            <AntTabs
              value={tabIndex}
              onChange={handleChange}
              aria-label="icon label tabs example"
            >
              <AntTab
                icon={<CloudDownloadOutlinedIcon />}
                iconPosition="start"
                label="Upload TimeSheet"
              />
              <AntTab
                icon={<SummarizeOutlinedIcon />}
                iconPosition="start"
                label="Generate Template"
              />
              <AntTab
                icon={<SummarizeOutlinedIcon />}
                iconPosition="start"
                label="Edit Hours"
              />
            </AntTabs>
          </Box>

          {/* Upload TimeSheets */}
          <div
            className={`tw-border tw-rounded-lg tw-p-2 tw-text-xs tw-shadow-md tw-leading-5 tw-w-[51rem] tw-mt-3 ${
              tabIndex === 0 ? "" : "hidden"
            }`}
          >
            <h4 className="Subheader">Upload Timesheet</h4>
            <div className="tw-flex tw-justify-center tw-mt-5 tw-mb-5">
              <FileInput.Root
                className="tw-w-[51rem]"
                onSelectedFiles={handleSelectedFiles}
                fileStatus={fileStatus}
              >
                <FileInput.Trigger />
                <div className="tw-max-h-[50vh] tw-overflow-y-auto">
                  <FileInput.FileList />
                </div>
                {dialogOpen && (
                  <div className="tw-max-h-[20vh] tw-overflow-y-auto tw-border tw-p-3 tw-rounded-lg tw-text-xs tw-mt-3">
                    <div className="tw-font-bold tw-mb-2 tw-text-red-500">
                      {dialogMessage.file}
                    </div>
                    {dialogMessage.message.map((line, index) => (
                      <>
                        <div
                          className={
                            dialogMessage.status?.[index] === "error"
                              ? "tw-text-red-700 tw-mt-2"
                              : dialogMessage.status?.[index] === "warning"
                              ? "tw-text-yellow-300 tw-mt-2"
                              : "tw-mt-2"
                          }
                        >
                          {dialogMessage.status?.[index]?.toUpperCase()} {"  "}
                          {selectedFile?.[index]?.name}
                        </div>
                        {line.split("\n").map((line, index) => (
                          <p
                            key={index}
                            style={{
                              fontSize: "0.75rem",
                              marginLeft: "10px",
                              fontFamily:
                                "-apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif",
                            }}
                          >
                            {line}
                          </p>
                        ))}
                      </>
                    ))}
                  </div>
                )}
                <FileInput.Control multiple />
              </FileInput.Root>
            </div>
            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
              <Button
                type="button"
                disabled={
                  !selectedFile || selectedFile.length <= 0 || uploadingHours
                }
                className="tw-w-[13%]"
                onClick={handleSendHours}
              >
                {uploadingHours ? (
                  <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" />
                ) : (
                  "Upload Hours"
                )}
              </Button>
            </div>
          </div>

          {/*Generator Template*/}
          <div
            className={`tw-border tw-rounded-lg tw-p-2 tw-text-xs tw-shadow-md tw-leading-5 tw-w-[51rem] tw-mt-3 ${
              tabIndex === 1 ? "" : "hidden"
            }`}
          >
            {hasPermission(roles, "Generate Template", "Access") && (
              <GeneratorTemplate plants={plants} roles={roles} />
            )}
          </div>

          {/* Edit Hours */}
          <div
            className={`tw-border tw-rounded-lg tw-p-2 tw-text-xs tw-shadow-md tw-leading-5 tw-w-[60rem] tw-mt-3 ${
              tabIndex === 2 ? "" : "hidden"
            }`}
          >
            {hasPermission(roles, "Hours Edit", "Access") && (
              <HoursEdit plants={plants} roles={roles} refresh={refresh} />
            )}
          </div>
        </div>
      </Box>
    </div>
  );
}
